export const CREATE_CLIENT_FIELDS = [
  "firstName",
  "middleName",
  "lastName",
  "personnummer",
  "email",
  "phone",
  "addressLine1",
  "addressLine2",
  "zipCode",
  "city",
] as const;

export type CreateClientField = (typeof CREATE_CLIENT_FIELDS)[number];

const ACCEPTED: ReadonlySet<string> = new Set<string>(CREATE_CLIENT_FIELDS);

function isCreateClientField(key: string): key is CreateClientField {
  return ACCEPTED.has(key);
}

const CREATE_CLIENT_REQUIRED = [
  "firstName",
  "lastName",
  "personnummer",
] as const satisfies readonly CreateClientField[];

export type CreateClientRequiredField = (typeof CREATE_CLIENT_REQUIRED)[number];

export function requiredStillEmpty(
  values: Partial<Record<CreateClientField, string | null | undefined>>,
): CreateClientRequiredField[] {
  return CREATE_CLIENT_REQUIRED.filter((field) => text(values[field]) === null);
}

export function halfANameArrived(
  values: Partial<Record<CreateClientField, string | null | undefined>>,
): boolean {
  const first = text(values.firstName) !== null;
  const last = text(values.lastName) !== null;
  return first !== last;
}

export interface UnstorableValue {
  path: string;
  value: string;
}

export interface PartitionedPrefill {
  fields: Partial<Record<CreateClientField, string>>;
  marked: CreateClientField[];
  unstorable: UnstorableValue[];
  unstorableOverflow: number;
}

const UNSTORABLE_SHOWN_MAX = 12;

function text(value: unknown): string | null {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "true" : "false";
  return null;
}

function leaves(value: unknown, path: string, out: UnstorableValue[]): void {
  const scalar = text(value);
  if (scalar !== null) {
    out.push({ path, value: scalar });
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      const joined = joinLeaves(item);
      if (joined !== null) out.push({ path: `${path}[${index}]`, value: joined });
    });
    return;
  }
  if (value !== null && typeof value === "object") {
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      leaves(child, `${path}.${key}`, out);
    }
  }
}

function joinLeaves(value: unknown): string | null {
  const scalar = text(value);
  if (scalar !== null) return scalar;
  if (value === null || typeof value !== "object") return null;
  const parts: UnstorableValue[] = [];
  leaves(value, "", parts);
  const joined = parts.map((p) => p.value).join(" · ");
  return joined || null;
}

export function partitionPrefill(read: Record<string, unknown>): PartitionedPrefill {
  const fields: Partial<Record<CreateClientField, string>> = {};
  const marked: CreateClientField[] = [];
  const extra: UnstorableValue[] = [];

  for (const [key, raw] of Object.entries(read)) {
    if (isCreateClientField(key)) {
      const value = text(raw);
      if (value === null) {
        continue;
      }
      fields[key] = value;
      marked.push(key);
      continue;
    }
    leaves(raw, key, extra);
  }

  marked.sort((a, b) => CREATE_CLIENT_FIELDS.indexOf(a) - CREATE_CLIENT_FIELDS.indexOf(b));

  return {
    fields,
    marked,
    unstorable: extra.slice(0, UNSTORABLE_SHOWN_MAX),
    unstorableOverflow: Math.max(0, extra.length - UNSTORABLE_SHOWN_MAX),
  };
}
